import type { UsageRequestLogEntry, UsageRequestUnitBilling } from '@nadoutong/contracts';
import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useDisplayRate } from '../api/useBilling.ts';
import { useKeysList } from '../api/useKeys.ts';
import { useUsageRequests } from '../api/useUsage.ts';
import { PanelError } from '../components/PanelError.tsx';
import {
  formatTimestamp,
  formatTokens,
  formatUsdMicrosOrCentsAsCnyMetered,
  unitAwareTokenCell,
  usdMicrosFromCentsFallback,
} from '../components/formatters.ts';
import { useClipboard } from '../hooks/useClipboard.tsx';
import { hasPermission, useSession } from '../hooks/useSession.tsx';
import { useLocale, useT } from '../i18n/index.ts';
import type { I18nKey } from '../i18n/index.ts';
import {
  Button,
  Chip,
  DateRangePicker,
  EmptyState,
  FormField,
  IconButton,
  Input,
  PageHeader,
  Pagination,
  Panel,
  Select,
  TBody,
  Table,
  TableSkeleton,
  Td,
  Th,
  THead,
  Tr,
  buttonClass,
  cx,
} from '../ui/index.ts';
import type { DateRangePreset } from '../ui/index.ts';
import { Check, ChevronDown, ChevronUp, Copy } from 'lucide-react';
import {
  apiKeyOptionLabel,
  dayRangeToIsoWindow,
  resolveRangeSelection,
  rollingDayRange,
  todayLocalDay,
} from './usage/format.ts';

/**
 * `/requests` — per-request billing metadata diagnostics (epic #897
 * PR 6).
 *
 * Every filter auto-applies with ONE semantic and mirrors into the URL
 * search params (`requestId`, `apiKeyId`, `status`, `range`; `replace`
 * so typing does not spam history). The request-id input debounces
 * 300ms and applies immediately on Enter. Because state lives in the
 * URL, the ⌘K palette's `/requests?requestId=…` deep link and the
 * Usage cross-links land pre-filtered, and a row's 「按此密钥筛选」
 * simply patches the params.
 *
 * The expanded detail row carries only fields NOT already visible in
 * the row (full request id, key id, protocol, billed state, error
 * category) plus the two cross-links; cost/latency/first-byte live in
 * the row itself.
 */

const PAGE_SIZE = 50;

type StatusFilter = '' | 'success' | 'error';

const ALL = 'all';
const DEFAULT_RANGE = '7d';
// Same UI-side cap as the /usage screens so the surfaces agree.
const MAX_RANGE_SPAN_DAYS = 90;
// Pre-picker URLs used `range=24h`; keep those deep links working.
const RANGE_ALIASES: Readonly<Record<string, string>> = { '24h': 'today' };

function requestsRangePresets(
  today: string,
  t: ReturnType<typeof useT>,
): readonly DateRangePreset[] {
  return [
    { id: 'today', label: t('usage.range.today'), range: rollingDayRange(today, 1) },
    { id: '7d', label: t('usage.range.7d'), range: rollingDayRange(today, 7) },
    { id: '30d', label: t('usage.range.30d'), range: rollingDayRange(today, 30) },
    { id: '90d', label: t('usage.range.90d'), range: rollingDayRange(today, 90) },
  ];
}

function parseStatus(value: string | null): StatusFilter {
  return value === 'success' || value === 'error' ? value : '';
}

export function Requests(): JSX.Element {
  const t = useT();
  const { locale } = useLocale();
  const { user } = useSession();
  // `/v1/keys` picks its own scope from this permission: without it
  // platform-api appends `AND COALESCE(assigned_account_id,
  // created_by_account_id) = <caller>`, so the dropdown can only ever
  // hold the reader's own keys; with it the response — and so the
  // dropdown — is the whole organization (`routes/v1-keys.ts`,
  // `ownOnly`). The flag gates no request and filters nothing here; it
  // decides which of those two sets the "all keys" option is claiming
  // to mean, which the single old label could not say (#14).
  const canReadOrgKeys = hasPermission(user, 'org:api_keys:read');
  const keysQ = useKeysList();
  const displayRateQ = useDisplayRate();
  const [searchParams, setSearchParams] = useSearchParams();

  const requestId = searchParams.get('requestId') ?? '';
  const apiKeyId = searchParams.get('apiKeyId') ?? '';
  const status = parseStatus(searchParams.get('status'));
  const today = todayLocalDay();
  const rangePresets = useMemo(() => requestsRangePresets(today, t), [today, t]);
  const rangeSelection = useMemo(
    () =>
      resolveRangeSelection({
        rangeParam: searchParams.get('range'),
        fromParam: searchParams.get('from'),
        toParam: searchParams.get('to'),
        presets: rangePresets,
        defaultPresetId: DEFAULT_RANGE,
        maxSpanDays: MAX_RANGE_SPAN_DAYS,
        today,
        aliases: RANGE_ALIASES,
      }),
    [searchParams, rangePresets, today],
  );

  const [requestIdInput, setRequestIdInput] = useState(requestId);
  const [offset, setOffset] = useState(0);
  const [expandedId, setExpandedId] = useState<string | null>(null);

  function patchParams(patch: Readonly<Record<string, string | null>>): void {
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams(previous);
        for (const [key, value] of Object.entries(patch)) {
          if (value === null || value.length === 0) next.delete(key);
          else next.set(key, value);
        }
        return next;
      },
      { replace: true },
    );
    setOffset(0);
    setExpandedId(null);
  }

  // Keep the input in sync when the URL changes from outside (palette
  // deep link, cross-links); a no-op while the user is typing because
  // the debounce below only fires when the values differ.
  useEffect(() => {
    setRequestIdInput(requestId);
  }, [requestId]);

  useEffect(() => {
    const trimmed = requestIdInput.trim();
    if (trimmed === requestId) return undefined;
    const timer = setTimeout(() => {
      patchParams({ requestId: trimmed });
    }, 300);
    return () => clearTimeout(timer);
  }, [requestIdInput, requestId]);

  const range = useMemo(() => dayRangeToIsoWindow(rangeSelection.range), [rangeSelection.range]);
  const requestsQ = useUsageRequests({
    ...range,
    requestId: requestId.length > 0 ? requestId : undefined,
    apiKeyId: apiKeyId.length > 0 ? apiKeyId : undefined,
    status: status === '' ? undefined : status,
    limit: PAGE_SIZE,
    offset,
  });
  const rows = requestsQ.data?.rows ?? [];
  const total = requestsQ.data?.total ?? 0;
  const cnyPerUsd = displayRateQ.data?.cnyPerUsd ?? null;
  const hasFilters = requestId.length > 0 || apiKeyId.length > 0 || status !== '';
  const page = Math.floor(offset / PAGE_SIZE) + 1;
  const pageCount = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const keyOptions = [
    {
      value: ALL,
      label: t(canReadOrgKeys ? 'requests.filter.key_all_org' : 'requests.filter.key_all'),
    },
    ...(keysQ.data?.keys ?? []).map((key) => ({
      value: key.id,
      label: apiKeyOptionLabel(key, t('usage.admin.filter.revoked_suffix')),
    })),
  ];

  return (
    <div data-testid="requests-page" className="flex flex-col gap-4">
      <PageHeader
        title={t('requests.title')}
        sub={t('requests.subtitle')}
        testId="requests-header"
        actions={
          <DateRangePicker
            value={rangeSelection.range}
            onChange={(nextRange, presetId) => {
              if (presetId !== null) patchParams({ range: presetId, from: null, to: null });
              else patchParams({ range: 'custom', from: nextRange.from, to: nextRange.to });
            }}
            presets={rangePresets}
            activePresetId={rangeSelection.presetId}
            maxSpanDays={MAX_RANGE_SPAN_DAYS}
            locale={locale}
            ariaLabel={t('requests.range.aria_label')}
            labels={{
              prevMonth: t('usage.range.prev_month'),
              nextMonth: t('usage.range.next_month'),
            }}
            testId="requests-range-picker"
          />
        }
      />

      <Panel caption={t('requests.filter.caption')} padded testId="requests-filter-bar">
        <div className="flex flex-wrap items-end gap-3">
          <FormField label={t('requests.filter.request_id')} fieldId="requests-filter-request-id">
            <Input
              id="requests-filter-request-id"
              mono
              value={requestIdInput}
              onChange={(event) => setRequestIdInput(event.currentTarget.value)}
              onKeyDown={(event) => {
                if (event.key === 'Enter') {
                  patchParams({ requestId: requestIdInput.trim() });
                }
              }}
              placeholder={t('requests.filter.request_id_placeholder')}
              className="w-72"
              data-testid="requests-filter-request-id"
            />
          </FormField>
          <FormField label={t('requests.filter.key')} fieldId="requests-filter-key">
            <Select
              value={apiKeyId.length === 0 ? ALL : apiKeyId}
              onValueChange={(next) => patchParams({ apiKeyId: next === ALL ? null : next })}
              options={keyOptions}
              ariaLabel={t('requests.filter.key')}
              className="w-56"
              testId="requests-filter-key"
            />
          </FormField>
          <FormField label={t('requests.filter.status')} fieldId="requests-filter-status">
            <Select
              value={status === '' ? ALL : status}
              onValueChange={(next) => patchParams({ status: next === ALL ? null : next })}
              options={[
                { value: ALL, label: t('requests.filter.status_all') },
                { value: 'success', label: t('requests.filter.status_success') },
                { value: 'error', label: t('requests.filter.status_error') },
              ]}
              ariaLabel={t('requests.filter.status')}
              className="w-40"
              testId="requests-filter-status"
            />
          </FormField>
          {hasFilters ? (
            <Button
              variant="ghost"
              onClick={() => patchParams({ requestId: null, apiKeyId: null, status: null })}
              data-testid="requests-filter-clear"
            >
              {t('requests.filter.clear')}
            </Button>
          ) : null}
        </div>
      </Panel>

      <Panel caption={t('requests.list.caption')} count={total} testId="requests-list">
        {requestsQ.isLoading ? (
          <div className="p-4">
            <TableSkeleton rows={6} testId="requests-loading" />
          </div>
        ) : requestsQ.isError ? (
          <div className="p-4">
            <PanelError
              message={t('requests.error')}
              retryLabel={t('usage.retry')}
              onRetry={() => {
                void requestsQ.refetch();
              }}
              testId="requests-error"
            />
          </div>
        ) : rows.length === 0 ? (
          hasFilters ? (
            <EmptyState
              testId="requests-empty-filtered"
              title={t('requests.empty.filtered.title')}
              hint={t('requests.empty.filtered.hint')}
              action={
                <Button
                  variant="secondary"
                  onClick={() => patchParams({ requestId: null, apiKeyId: null, status: null })}
                >
                  {t('requests.filter.clear')}
                </Button>
              }
            />
          ) : (
            <EmptyState
              testId="requests-empty"
              title={t('requests.empty.title')}
              hint={t('requests.empty.hint')}
              action={
                <Link to="/access/docs" className={buttonClass('secondary')}>
                  {t('requests.empty.docs')}
                </Link>
              }
            />
          )
        ) : (
          <>
            <RequestTable
              rows={rows}
              expandedId={expandedId}
              cnyPerUsd={cnyPerUsd}
              onToggle={(id) => setExpandedId((current) => (current === id ? null : id))}
              onFilterByKey={(id) => patchParams({ apiKeyId: id })}
            />
            <div className="flex items-center justify-end border-t border-line-soft px-4 py-3">
              <Pagination
                page={page}
                pageCount={pageCount}
                onPageChange={(next) => {
                  setOffset((next - 1) * PAGE_SIZE);
                  setExpandedId(null);
                }}
                prevLabel={t('usage.pagination.previous')}
                nextLabel={t('usage.pagination.next')}
                ariaLabel={t('requests.pagination.aria')}
                testId="requests-pagination"
              />
            </div>
          </>
        )}
      </Panel>
    </div>
  );
}

type BillingState = 'billed' | 'not_billed' | 'pending';

/**
 * Under async rating (#1410 P3) a request is visible before its price is
 * known, so a zero cost no longer means "not billed" — it can also mean "not
 * priced yet". Reading the cost alone would tell a paying customer they were
 * not charged, minutes before the charge lands.
 *
 * `ratingStatus` is optional on the wire: a gateway that predates P3, or the
 * one-deploy window where platform has shipped and the gateway has not, omits
 * it. Every such row is final — hence `'rated'` as the fallback, never
 * `'pending'`, which would paint the entire history as unsettled.
 */
function billingState(row: UsageRequestLogEntry): BillingState {
  const status = row.ratingStatus ?? 'rated';
  if (status === 'pending') return 'pending';
  return usdMicrosFromCentsFallback(row.costUsdMicros, row.costUsdCents) > 0
    ? 'billed'
    : 'not_billed';
}

/** #1670 token-column discriminator; the shared logic lives in formatters. */
function tokenCell(row: UsageRequestLogEntry, count: number): string {
  return unitAwareTokenCell(row, formatTokens(count));
}

/** `3 张 · ¥x` / `未交付，未收费` — the one-line unit billing summary. */
function unitBillingSummary(
  unitBilling: UsageRequestUnitBilling,
  cnyPerUsd: number | null,
  t: ReturnType<typeof useT>,
): string {
  if (unitBilling.kind === 'not_charged') {
    return t(`requests.unit_billing.not_charged.${unitBilling.reason}`);
  }
  return unitBilling.entries
    .map((entry) =>
      t('requests.unit_billing.charged', {
        quantity: entry.quantity,
        unit: t(`requests.unit_billing.unit.${entry.unit}`),
        amount: formatUsdMicrosOrCentsAsCnyMetered(entry.amountUsdMicros, 0, cnyPerUsd),
      }),
    )
    .join(' + ');
}

function RequestTable(props: {
  readonly rows: readonly UsageRequestLogEntry[];
  readonly expandedId: string | null;
  readonly cnyPerUsd: number | null;
  readonly onToggle: (id: string) => void;
  readonly onFilterByKey: (apiKeyId: string) => void;
}): JSX.Element {
  const t = useT();
  return (
    <Table className="rounded-none border-0" testId="requests-table">
      <THead>
        <Tr interactive={false}>
          <Th className="w-10">
            <span className="sr-only">{t('requests.expand')}</span>
          </Th>
          <Th>{t('requests.column.time')}</Th>
          <Th>{t('requests.column.request')}</Th>
          <Th>{t('requests.column.key')}</Th>
          <Th>{t('requests.column.model')}</Th>
          <Th>{t('requests.column.status')}</Th>
          <Th num>{t('requests.column.duration')}</Th>
          <Th num>{t('requests.column.first_byte')}</Th>
          <Th num>{t('requests.column.input')}</Th>
          <Th num>{t('requests.column.output')}</Th>
          <Th num>{t('requests.column.cache_read')}</Th>
          <Th num>{t('requests.column.cache_write')}</Th>
          <Th num>{t('requests.column.cost')}</Th>
        </Tr>
      </THead>
      <TBody>
        {props.rows.map((row) => {
          const isExpanded = props.expandedId === row.id;
          return (
            <Fragment key={row.id}>
              <Tr
                className="cursor-pointer"
                data-testid={`requests-row-${row.id}`}
                onClick={() => props.onToggle(row.id)}
              >
                <Td className="max-md:hidden">
                  <IconButton
                    size="sm"
                    label={isExpanded ? t('requests.collapse') : t('requests.expand')}
                    onClick={(event) => {
                      event.stopPropagation();
                      props.onToggle(row.id);
                    }}
                    data-testid={`requests-toggle-${row.id}`}
                  >
                    {isExpanded ? (
                      <ChevronUp className="h-4 w-4" />
                    ) : (
                      <ChevronDown className="h-4 w-4" />
                    )}
                  </IconButton>
                </Td>
                <Td mono heading>
                  {formatTimestamp(row.createdAt)}
                </Td>
                <Td mono label={t('requests.column.request')}>
                  {shortRequestId(row.requestId)}
                </Td>
                <Td emphasis label={t('requests.column.key')}>
                  {keyDisplayName(row.apiKeyName, row.apiKeyPrefix, t)}
                </Td>
                <Td mono label={t('requests.column.model')}>
                  {row.model}
                </Td>
                <Td label={t('requests.column.status')}>
                  <Chip
                    tone={row.statusCode >= 400 ? 'bad' : 'ok'}
                    testId={`requests-status-${row.id}`}
                  >
                    {String(row.statusCode)}
                  </Chip>
                </Td>
                <Td num label={t('requests.column.duration')}>
                  {formatDuration(row.latencyMs, t)}
                </Td>
                <Td num label={t('requests.column.first_byte')}>
                  {formatDuration(row.clientResponseElapsedMs, t)}
                </Td>
                <Td num label={t('requests.column.input')}>
                  {tokenCell(row, row.inputTokens)}
                </Td>
                <Td num label={t('requests.column.output')}>
                  {tokenCell(row, row.outputTokens)}
                </Td>
                <Td num label={t('requests.column.cache_read')}>
                  {tokenCell(row, row.cacheReadTokens)}
                </Td>
                <Td num label={t('requests.column.cache_write')}>
                  {tokenCell(row, row.cacheWriteTokens)}
                </Td>
                <Td num emphasis label={t('requests.column.cost')}>
                  {billingState(row) === 'pending' ? (
                    <span className="text-paper-mid">{t('requests.cost.pending')}</span>
                  ) : row.unitBilling?.kind === 'not_charged' ? (
                    // Wording is reason-specific on purpose: `not_delivered`
                    // is final ("未收费"), while `pricing_review` is a
                    // reversible hold — saying "not charged" there would
                    // promise an outcome the review may still change.
                    <span
                      className="text-paper-mid"
                      data-testid={`requests-cost-not-charged-${row.id}`}
                    >
                      {t(`requests.cost.not_charged.${row.unitBilling.reason}`)}
                    </span>
                  ) : (
                    formatUsdMicrosOrCentsAsCnyMetered(
                      row.costUsdMicros,
                      row.costUsdCents,
                      props.cnyPerUsd,
                    )
                  )}
                </Td>
              </Tr>
              {isExpanded ? (
                <Tr interactive={false} data-testid={`requests-row-details-${row.id}`}>
                  <Td colSpan={13} stack className="bg-well">
                    <RequestDetails
                      row={row}
                      cnyPerUsd={props.cnyPerUsd}
                      onFilterByKey={props.onFilterByKey}
                    />
                  </Td>
                </Tr>
              ) : null}
            </Fragment>
          );
        })}
      </TBody>
    </Table>
  );
}

function RequestDetails(props: {
  readonly row: UsageRequestLogEntry;
  readonly cnyPerUsd: number | null;
  readonly onFilterByKey: (apiKeyId: string) => void;
}): JSX.Element {
  const t = useT();
  const { row } = props;
  const { copy, copied } = useClipboard();
  return (
    <div className="flex flex-col gap-3 py-1">
      <dl className="m-0 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
        <div className="flex min-w-0 flex-col gap-1 xl:col-span-2">
          <dt className="text-label text-paper-mid">{t('requests.detail.request_id')}</dt>
          <dd className="m-0 flex min-w-0 items-center gap-2">
            <span className="break-all font-mono text-dense text-paper">{row.requestId}</span>
            <IconButton
              size="sm"
              label={t('requests.detail.copy_request_id')}
              onClick={() => {
                void copy(row.requestId);
              }}
              data-testid={`requests-copy-request-id-${row.id}`}
            >
              {copied ? <Check className="h-4 w-4 text-ok" /> : <Copy className="h-4 w-4" />}
            </IconButton>
          </dd>
        </div>
        <DetailField label={t('requests.detail.api_key_id')} value={row.apiKeyId} />
        <DetailField label={t('requests.detail.protocol')} value={row.protocol} />
        <DetailField
          label={t('requests.detail.billing')}
          value={t(`requests.detail.billing.${billingState(row)}`)}
          testId={`requests-detail-billing-${row.id}`}
        />
        {row.unitBilling !== undefined ? (
          <DetailField
            label={t('requests.detail.unit_billing')}
            value={unitBillingSummary(row.unitBilling, props.cnyPerUsd, t)}
            testId={`requests-detail-unit-billing-${row.id}`}
          />
        ) : null}
        <DetailField
          label={t('requests.detail.error_category')}
          value={statusCategory(row.statusCode, t)}
          testId={`requests-detail-error-category-${row.id}`}
        />
      </dl>
      <div className="flex flex-wrap gap-2">
        <Button
          variant="ghost"
          size="sm"
          onClick={() => props.onFilterByKey(row.apiKeyId)}
          data-testid={`requests-filter-by-key-${row.id}`}
        >
          {t('requests.detail.filter_by_key')}
        </Button>
        <Link
          to={`/usage?apiKeyId=${encodeURIComponent(row.apiKeyId)}`}
          className={cx(buttonClass('ghost', 'sm'))}
          data-testid={`requests-view-key-usage-${row.id}`}
        >
          {t('requests.detail.view_key_usage')}
        </Link>
      </div>
    </div>
  );
}

function DetailField(props: {
  readonly label: string;
  readonly value: string;
  readonly testId?: string;
}): JSX.Element {
  return (
    <div className="flex min-w-0 flex-col gap-1" data-testid={props.testId}>
      <dt className="text-label text-paper-mid">{props.label}</dt>
      <dd className="m-0 break-all font-mono text-dense text-paper">{props.value}</dd>
    </div>
  );
}

function formatDuration(
  value: number | null,
  t: (key: I18nKey, vars?: Record<string, string | number>) => string,
): string {
  return value === null ? '--' : t('requests.duration.ms', { value });
}

function statusCategory(
  statusCode: number,
  t: (key: I18nKey, vars?: Record<string, string | number>) => string,
): string {
  if (statusCode === 429) return t('requests.status_category.rate_limited');
  if (statusCode >= 500) return t('requests.status_category.service_error');
  if (statusCode >= 400) return t('requests.status_category.request_error');
  return t('requests.status_category.success');
}

function shortRequestId(value: string): string {
  if (value.length <= 18) return value;
  return `${value.slice(0, 10)}…${value.slice(-6)}`;
}

function keyDisplayName(
  name: string | null,
  prefix: string | null,
  t: (key: I18nKey, vars?: Record<string, string | number>) => string,
): string {
  if (typeof name === 'string' && name.length > 0) return name;
  if (typeof prefix === 'string' && prefix.length > 0) return t('requests.key.unnamed');
  return t('requests.key.unknown');
}
